import { BaseConfig } from "../config.interface";
import { Size, Validate } from "../util";

export type NumberInputAffix = string | "currency";
interface IInputNumberConfig {
    label?: string;
    autocomplete?: string;
    min?: number;
    max?: number;
    step?: number;
    buttons?: boolean;
    fractions?: number;
    prefix?: NumberInputAffix;
    suffix?: NumberInputAffix;
    clear?: boolean;
    mode: "number" | "decimal";
    validate: Validate;
    size: Size;
}

export class InputNumberConfig extends BaseConfig<IInputNumberConfig> implements IInputNumberConfig {
    /**
     * Label of the element.
     */
    label?: string;
    /**
     * Browser autocomplete attribute.
     */

    autocomplete?: string;
    /**
     * Min value of the input.
     */
    min?: number;
    /**
     * Max value of the input.
     */
    max?: number;
    /**
     * Step size of the input.
     */
    step?: number;
    /**
     * Show buttons for changing input value
     */
    buttons?: boolean;
    /**
     * Determine the fraction size.
     */
    fractions?: number;
    /**
     * Display a prefix label before the input
     */
    prefix?: NumberInputAffix;
    /**
     * Display a suffix label after the input
     */
    suffix?: NumberInputAffix;
    /**
     * Show a clear button.
     */
    clear?: boolean;
    /**
     * Allow only integers or decimals
     */
    mode: "number" | "decimal";
    /**
     * Show validation status already in `pristine` state or after it has been marked as `dirty`.
     */
    validate: Validate;
    size: Size;

    /**
     * @theme Base
     */
    static baseTheme = {
        /**
         * Default configuration.
         * Number value with step of 1.
         */
        Default: new InputNumberConfig({
            mode:      "number",
            step:      1,
            clear:     true,
            validate:  "dirty",
            size:      "small",
            fractions: 0,
        }),

        Full: new InputNumberConfig({
            mode:      "number",
            step:      1,
            clear:     true,
            validate:  "dirty",
            size:      "full",
            fractions: 0,
        }),


        Buttons: new InputNumberConfig({
            mode:      "number",
            step:      1,
            buttons:   true,
            clear:     true,
            validate:  "dirty",
            size:      "medium",
            fractions: 0,
        }),

        /**
         * Positive integer value with step of 1.
         */
        Positive: new InputNumberConfig({
            mode:     "number",
            min:      1,
            step:     1,
            clear:    true,
            validate: "dirty",
            size:     "small",
        }),

        /**
         * Decimal value with step of 0.5
         */
        Decimal: new InputNumberConfig({
            mode:     "decimal",
            step:     0.5,
            clear:    true,
            validate: "dirty",
            buttons:  true,
            size:     "medium",
        }),

        Days: new InputNumberConfig({
            mode:     "decimal",
            step:     0.5,
            clear:    true,
            validate: "dirty",
            buttons:  true,
            size:     "medium",
        }),

        /**
         * Decimal value with enforced decimal places.
         */
        DecimalFull: new InputNumberConfig({
            mode:      "decimal",
            step:      1,
            clear:     true,
            validate:  "dirty",
            fractions: 2,
            size:      "small",
        }),

        /**
         * Decimal value with enforced decimal places.
         */
        VAT: new InputNumberConfig({
            mode:     "decimal",
            min:      0,
            max:      100,
            step:     0.1,
            clear:    true,
            validate: "dirty",
            size:     "small",
        }),
        /**
         * Decimal value with enforced decimal places.
         */
        OvertimeLimit: new InputNumberConfig({
            mode:      "decimal",
            min:       0,
            max:       9999,
            fractions: 2,
            clear:     true,
            validate:  "dirty",
            size:      "small",
        }),
        /**
         * Use this for currency input
         * Prefixes field with currency
         */
        Currency: new InputNumberConfig({
            mode:      "decimal",
            step:      1,
            clear:     true,
            validate:  "dirty",
            fractions: 2,
            prefix:    "currency",
            size:      "small",
        }),

        /**
         * Use this for currency input
         * Prefixes field with currency
         */
        CurrencyMedium: new InputNumberConfig({
            mode:      "decimal",
            step:      1,
            clear:     true,
            validate:  "dirty",
            fractions: 2,
            prefix:    "currency",
            size:      "medium",
        }),

        /**
         * Use this for currency input
         * Prefixes field with currency
         */
        CurrencyLarge: new InputNumberConfig({
            mode:      "decimal",
            step:      1,
            clear:     true,
            validate:  "dirty",
            fractions: 2,
            prefix:    "currency",
            size:      "large",
        }),

        /**
         * Positive currency with step of 1.
         */
        PositiveCurrency: new InputNumberConfig({
            mode:      "decimal",
            min:       1,
            step:      1,
            clear:     true,
            validate:  "dirty",
            fractions: 2,
            prefix:    "currency",
            size:      "small",
        }),

        Pristine: new InputNumberConfig({
            mode:      "decimal",
            step:      1,
            clear:     true,
            validate:  "pristine",
            fractions: 0,
            size:      "small",
        }),

        CurrencyMediumPristine: new InputNumberConfig({
            mode:      "decimal",
            min:       1,
            step:      1,
            clear:     true,
            validate:  "pristine",
            fractions: 2,
            prefix:    "currency",
            size:      "medium",
        }),
    };
}